<template>
  <Dialog
    :open="modelValue"
    as="div"
    class="relative z-50"
    @close="emit('update:modelValue', false)"
  >
    <div
      class="fixed inset-0 bg-black/40"
      aria-hidden="true"
    />
    <div class="fixed inset-0 flex items-center justify-center px-2">
      <DialogPanel class="flex w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white p-6 shadow-2xl md:p-8">
        <button
          class="mb-6 ml-auto rounded-full p-1 transition-colors hover:bg-gray-100 focus:outline-none"
          @click="emit('update:modelValue', false)"
        >
          <XMarkIcon class="size-6 text-gray-400 hover:text-gray-600" />
        </button>
        <div class="mb-6 text-center">
          <DialogTitle class="mb-3 font-serif text-3xl font-semibold text-gray-800 md:text-4xl">
            Compartir producto
          </DialogTitle>
          <DialogDescription class="text-base text-gray-600 md:text-lg">
            Envía una vista previa de este producto a cualquier persona.
          </DialogDescription>
        </div>

        <div class="mb-6 flex items-center space-x-4">
          <div class="relative">
            <img
              :src="productImageToShow"
              :alt="product?.name"
              class="size-20 shrink-0 rounded-xl object-cover shadow-md md:size-24"
            />
            <div class="absolute -right-2 -top-2 rounded-full bg-lk-green p-1">
              <ShareIcon class="size-4 text-white" />
            </div>
          </div>
          <div class="min-w-0 flex-1">
            <h4 class="mb-2 line-clamp-2 text-lg font-semibold text-gray-900 md:text-xl">
              {{ product?.name }}
            </h4>
            <p class="mb-1 text-base text-gray-600">
              {{ product?.makerName }}
            </p>
            <div class="flex items-center text-sm text-gray-500">
              <span class="rounded-full bg-lk-light-green px-2 py-1 text-xs text-white">Vista previa</span>
            </div>
          </div>
        </div>
        <button
          class="group relative overflow-hidden rounded-xl bg-gradient-to-r from-lk-green to-lk-light-green p-4 text-white shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-xl active:scale-[0.98]"
          @click="copyProductLink"
        >
          <div class="flex items-center justify-center space-x-3">
            <div class="rounded-full bg-white/20 p-2">
              <ClipboardDocumentCheckIcon
                v-if="linkCopied"
                class="size-6 text-white"
              />
              <ClipboardDocumentIcon
                v-else
                class="size-6 text-white"
              />
            </div>
            <span class="text-lg font-medium">
              {{ linkCopied ? '¡Enlace copiado!' : 'Copiar enlace' }}
            </span>
          </div>
          <div
            v-if="linkCopied"
            class="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 transition-opacity duration-300"
          />
          <div class="absolute inset-0 bg-white/10 opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
        </button>
      </DialogPanel>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { XMarkIcon, ShareIcon } from '@heroicons/vue/24/solid';
import { ClipboardDocumentIcon, ClipboardDocumentCheckIcon } from '@heroicons/vue/24/outline';
import { Dialog, DialogPanel, DialogTitle, DialogDescription } from '@headlessui/vue';
import useMessageStore from 'stores/message-store';

interface LokalProductShareDialogProps {
  modelValue: boolean;
  product?: Product;
}

const props = withDefaults(defineProps<LokalProductShareDialogProps>(), {
  modelValue: false,
  product: undefined,
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const messageStore = useMessageStore();
const linkCopied = ref(false);

const productImageToShow = computed(() => {
  if (!props.product) return '';
  return props.product.images?.[0]?.derivatives?.jpgMd?.url || '';
});

const productUrl = computed(() => {
  if (!props.product) return '';
  const protocol = process.env.APPLICATION_HOST?.includes('http') ? '' : 'https://';
  const host = `${protocol}${process.env.APPLICATION_HOST}`;
  return `${host}/products/${props.product.id}`;
});

function copyProductLink() {
  navigator.clipboard.writeText(productUrl.value).then(() => {
    linkCopied.value = true;
    messageStore.showMessage('Enlace del producto copiado al portapapeles', 'success', 'short');
    setTimeout(() => {
      linkCopied.value = false;
    }, 500);
  });
}
</script>
